import { type QueryClient, type QueryKey, useQueries, useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api/client';
import { ALL_STATUS_FILTERS, type StatusFilter, type StatusFilterValue } from './filters';
import { type ApiDealListItem, type Deal, type PagedResult, toDeal } from './types';

/** Prefixo do cache das listagens; as entradas reais são ['deals', status] (uma por status). */
export const DEALS_QUERY_KEY = ['deals'] as const;

export function dealsQueryKey(status: StatusFilter) {
  return ['deals', status] as const;
}

export interface DealsPage {
  /** Itens do status pedido, exceto ClosedToPortfolio. */
  deals: Deal[];
  /** Contagem total do status no servidor (PagedResult.total), NÃO items.length. */
  total: number;
  /**
   * `fetchAllPages` não conseguiu reconstruir a base inteira do status: bateu no teto de páginas
   * (MAX_PAGES) ou a base mudou no meio da busca (cobertura ficou abaixo do total). Enquanto a
   * base for estável e < MAX_PAGES * MaxTake (2.000), isto é sempre false — é rede de segurança,
   * não estado normal. Quando virar true, a tela e o CSV estão incompletos.
   */
  truncated: boolean;
}

// Teto de páginas por status: nunca busca mais que MAX_PAGES * MaxTake(200) = 2.000 deals de um
// status só. Guarda contra `total` vir estranho (bug de servidor) virando loop de fato ilimitado.
const MAX_PAGES = 10;

/**
 * Busca TODAS as páginas de um status, mesmo além de MaxTake=200 (backend/PagedResult.cs). A base
 * de prod já tem um status (Descartado) acima de 200 — confirmado ao vivo (chip satura em "200").
 * Página 1 dá o `total`; as páginas restantes (skip=200,400,...) são buscadas em paralelo, já que
 * o número de páginas é conhecido de antemão. Dedupe por id: se a base mudar entre a 1ª página e as
 * seguintes (ex.: alguém descarta um deal no meio da busca), a janela de skip pode se sobrepor —
 * o dedupe evita duplicata, e `truncated` (calculado no `select`, a partir de items.length < total)
 * sinaliza se algo ficou de fora.
 */
async function fetchAllPages(status: StatusFilter): Promise<PagedResult<ApiDealListItem>> {
  const first = await apiFetch<PagedResult<ApiDealListItem>>(`/api/deals?auxStatus=${status}&take=200`);
  const totalPages = Math.min(Math.ceil(first.total / 200), MAX_PAGES);
  if (totalPages <= 1) return first;

  const rest = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, i) => {
      const skip = (i + 1) * 200;
      return apiFetch<PagedResult<ApiDealListItem>>(`/api/deals?auxStatus=${status}&take=200&skip=${skip}`);
    }),
  );

  const seen = new Set<string>();
  const items: ApiDealListItem[] = [];
  for (const page of [first, ...rest]) {
    for (const item of page.items) {
      if (seen.has(item.id)) continue; // duplicata entre páginas (base mudou no meio da busca)
      seen.add(item.id);
      items.push(item);
    }
  }

  return { items, total: first.total, skip: 0, take: items.length };
}

/** Opções da query de UM status — compartilhadas por useDeals (1 status) e usePipelineDeals (N). */
function dealsQueryOptions(status: StatusFilter) {
  return {
    queryKey: dealsQueryKey(status),
    queryFn: () => fetchAllPages(status),
    select: (page: PagedResult<ApiDealListItem>): DealsPage => ({
      deals: page.items.map(toDeal).filter((d) => d.auxiliaryStatus !== 'ClosedToPortfolio'),
      total: page.total,
      truncated: page.items.length < page.total,
    }),
  };
}

/**
 * Carrega os deals de UM status (server-side: `?auxStatus=...`, reconstruindo a base inteira do
 * status via `fetchAllPages` quando ela passa de MaxTake=200 — ver `fetchAllPages`). Exclui
 * ClosedToPortfolio (vivem no Portfólio). `total` vem do PagedResult.
 *
 * Cache por status (['deals', status]); as mutations otimistas escrevem em todas as páginas via
 * prefixo (ver patchDealInCaches). Default 'Active' para os consumidores sem contexto de filtro
 * (Sidebar, DealDrawer, TarefasPage, TaskModal) — hoje sempre 1 request só, pois Active cabe em
 * MaxTake.
 */
export function useDeals(status: StatusFilter = 'Active') {
  return useQuery(dealsQueryOptions(status));
}

/** Resultado agregado do Pipeline — um status ou os três de "Todos", já concatenados. */
export interface PipelineDeals {
  /** Deals de todos os status pedidos, na ordem de ALL_STATUS_FILTERS. */
  deals: Deal[];
  /** Soma dos totais do servidor dos status pedidos. */
  total: number;
  /** Algum dos status pedidos veio truncado — a visão está incompleta. */
  truncated: boolean;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

/**
 * Deals da tela do Pipeline. Para 'All' dispara UMA query por status em paralelo e concatena no
 * cliente — não existe `auxStatus` múltiplo na API (DEC-038 manteve status único; a paginação
 * completa por status é o que resolve a soma dos três não caber numa página só).
 *
 * Reusa as MESMAS chaves de cache ['deals', status] do useDeals: as mutations otimistas por
 * prefixo (patchDealInCaches) continuam alcançando estas páginas sem nenhuma mudança.
 *
 * `combine` mantém a identidade do array `deals` estável entre renders quando o conteúdo não
 * muda (memoização via replaceEqualDeep no QueriesObserver do TanStack Query) — sem isso, os
 * `useMemo` de PipelinePage (filtros, KPIs, opções de líder/tese) recalculariam a cada render.
 * O dedupe por id aqui é defensivo: um patch otimista marca o status do item em TODAS as páginas
 * em cache antes do invalidate assentar (achado F12/revisão 2026-07, mesma família), então há uma
 * janela transitória em que o mesmo deal pode aparecer na página antiga e na nova ao mesmo tempo.
 */
export function usePipelineDeals(status: StatusFilterValue): PipelineDeals {
  const statuses = status === 'All' ? ALL_STATUS_FILTERS : [status];
  return useQueries({
    queries: statuses.map((s) => dealsQueryOptions(s)),
    combine: (results): PipelineDeals => {
      const seen = new Set<string>();
      const deals: Deal[] = [];
      for (const r of results) {
        for (const d of r.data?.deals ?? []) {
          if (seen.has(d.id)) continue;
          seen.add(d.id);
          deals.push(d);
        }
      }
      return {
        deals,
        total: results.reduce((sum, r) => sum + (r.data?.total ?? 0), 0),
        truncated: results.some((r) => r.data?.truncated === true),
        isLoading: results.some((r) => r.isLoading),
        isError: results.some((r) => r.isError),
        refetch: () => {
          for (const r of results) void r.refetch();
        },
      };
    },
  });
}

/**
 * Snapshot CIRÚRGICO do item `id` em cada página de status onde ele aparece — guarda só o valor do
 * item, não a página inteira (achado F12, revisão 2026-07). O snapshot antigo (página inteira)
 * fazia o rollback de uma mutation reverter TAMBÉM o patch otimista de outra mutation concorrente
 * em outro deal, se ela tivesse aplicado entre este snapshot e o erro — apagando visualmente uma
 * mudança que na verdade tinha dado certo no servidor, até o próximo refetch corrigir a tela.
 */
export function snapshotDealItem(qc: QueryClient, id: string) {
  const pages = qc.getQueriesData<PagedResult<ApiDealListItem>>({ queryKey: DEALS_QUERY_KEY });
  const byKey = new Map<QueryKey, ApiDealListItem>();
  for (const [key, data] of pages) {
    const item = data?.items.find((it) => it.id === id);
    if (item) byKey.set(key, item);
  }
  return byKey;
}

/**
 * Restaura só o item `id` nas páginas onde existia no snapshot (rollback em erro) — não sobrescreve
 * a página inteira, então preserva qualquer patch otimista de OUTRO item aplicado nesse meio-tempo.
 */
export function restoreDealItem(qc: QueryClient, id: string, snapshot: ReturnType<typeof snapshotDealItem>) {
  for (const [key, priorItem] of snapshot) {
    qc.setQueryData<PagedResult<ApiDealListItem>>(key, (prev) =>
      prev ? { ...prev, items: prev.items.map((it) => (it.id === id ? priorItem : it)) } : prev,
    );
  }
}

/** Aplica um patch a um deal em TODAS as páginas de status em cache (update otimista por prefixo). */
export function patchDealInCaches(
  qc: QueryClient,
  id: string,
  patch: (item: ApiDealListItem) => ApiDealListItem,
) {
  qc.setQueriesData<PagedResult<ApiDealListItem>>({ queryKey: DEALS_QUERY_KEY }, (prev) =>
    prev ? { ...prev, items: prev.items.map((it) => (it.id === id ? patch(it) : it)) } : prev,
  );
}
